import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import {
  ArrowRight,
  Users,
  Crown,
  Shield,
  TrendingUp,
  CheckCircle,
  Star,
  Home,
} from 'lucide-react';
import { ImplementationTimelineModal } from '../../../../montana-demo/components/governance/timeline/ImplementationTimelineModal';

// Import all our governance components from the montana-demo components directory
import { GovernanceHeroSection } from '../../../../montana-demo/components/governance/hero/GovernanceHeroSection';
import { CompactHeroSection } from '../../../../montana-demo/components/governance/hero/CompactHeroSection';
import { LegacyComparison } from '../../../../montana-demo/components/governance/comparison/LegacyComparison';
import { GovernanceEfficiencyCalculator } from '../../../../montana-demo/components/governance/comparison/GovernanceEfficiencyCalculator';
import { VisualizationShowcase } from '../../../../montana-demo/components/governance/visualization/VisualizationShowcase';
import { DigitalEnforcementBenefits } from '../../../../montana-demo/components/governance/enforcement/DigitalEnforcementBenefits';
import { InteractiveDemoShowcase } from '../../../../montana-demo/components/governance/demo/InteractiveDemoShowcase';

interface PageSection {
  id: string;
  title: string;
  description: string;
  component: React.ReactNode;
  featured: boolean;
}
interface GovernanceCommunityPageProps {
  /** Hide the internal fixed demo header so outer layout header is visible */
  disableInternalHeader?: boolean;
  /** Adjust top padding offset (e.g., if global header already occupies space) */
  topOffsetPx?: number;
}

export default function GovernanceCommunityPage({
  disableInternalHeader = false,
  topOffsetPx = 80,
}: GovernanceCommunityPageProps) {
  const [currentSection, setCurrentSection] = useState<string>('hero');
  const [completedSections, setCompletedSections] = useState<Set<string>>(new Set());
  const [heroVariant, setHeroVariant] = useState<'full' | 'compact'>('full');
  const [showTimelineModal, setShowTimelineModal] = useState(false);
  const navigate = useNavigate();

  const sections: PageSection[] = useMemo(
    () => [
      {
        id: 'hero',
        title: 'Welcome to Digital Governance',
        description: 'See how digital governance transforms community management',
        component: heroVariant === 'full' ? <GovernanceHeroSection /> : <CompactHeroSection />,
        featured: true,
      },
      {
        id: 'comparison',
        title: 'How Governance Works',
        description:
          'The organizational framework that eliminates "who\'s responsible for what" confusion',
        component: <LegacyComparison />,
        featured: true,
      },
      {
        id: 'calculator',
        title: 'ROI Calculator',
        description: 'Calculate the financial benefits for your 406-member community',
        component: <GovernanceEfficiencyCalculator />,
        featured: true,
      },
      // COMMENTED OUT - Section 4 temporarily disabled to reduce information overload
      // {
      //   id: 'structure',
      //   title: 'Governance Structure',
      //   description: 'Explore interactive visualizations of community leadership',
      //   component: <VisualizationShowcase />,
      //   featured: true
      // },
      // COMMENTED OUT - Digital Enforcement section temporarily disabled to reduce redundancy and information overload
      // {
      //   id: 'enforcement',
      //   title: 'Digital Enforcement',
      //   description: 'Experience automated compliance and transparency',
      //   component: <DigitalEnforcementBenefits />,
      //   featured: true
      // },
      // COMMENTED OUT - Interactive Demos section temporarily disabled to reduce information overload
      // {
      //   id: 'demos',
      //   title: 'Interactive Demos',
      //   description: 'Try digital governance hands-on with real scenarios',
      //   component: <InteractiveDemoShowcase />,
      //   featured: true
      // }
    ],
    [heroVariant],
  );

  // Auto-scroll to sections and mark as viewed
  useEffect(() => {
    const handleScroll = () => {
      const sectionElements = sections
        .map((section) => ({
          id: section.id,
          element: document.getElementById(`section-${section.id}`),
        }))
        .filter((item) => item.element);

      const currentViewport = window.innerHeight;
      let activeSection = '';

      for (const item of sectionElements) {
        const rect = item.element!.getBoundingClientRect();
        if (rect.top <= currentViewport * 0.5 && rect.bottom >= currentViewport * 0.5) {
          activeSection = item.id;
          break;
        }
      }

      if (activeSection && activeSection !== currentSection) {
        setCurrentSection(activeSection);
        setCompletedSections((prev) => new Set([...prev, activeSection]));
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [currentSection, sections]);

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(`section-${sectionId}`);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const featuredSections = sections.filter((s) => s.featured);
  const progressPercentage = (completedSections.size / sections.length) * 100;

  const containerTopPad = disableInternalHeader ? topOffsetPx : 80;

  return (
    <div className="min-h-screen bg-linear-to-br from-gray-50 to-blue-50">
      {/* Internal Fixed Navigation Header (optional) */}
      {!disableInternalHeader && (
        <div className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-xs border-b shadow-xs">
          <div className="container mx-auto px-4 py-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-2">
                  <Home className="w-5 h-5 text-blue-600" />
                  <span className="font-semibold">Digital Governance Demo</span>
                </div>
                <Badge className="bg-blue-100 text-blue-800">406-Member Community</Badge>
              </div>

              <div className="flex items-center gap-4">
                {/* Progress Indicator */}
                <div className="flex items-center gap-2">
                  <div className="w-24 bg-gray-200 rounded-full h-2">
                    <div
                      className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                      style={{ width: `${progressPercentage}%` }}
                    />
                  </div>
                  <span className="text-sm font-medium">{Math.round(progressPercentage)}%</span>
                </div>

                {/* Hero Variant Toggle */}
                <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1">
                  <Button
                    variant={heroVariant === 'full' ? 'default' : 'ghost'}
                    size="sm"
                    className="h-7 px-2 text-xs"
                    onClick={() => setHeroVariant('full')}
                  >
                    Full Hero
                  </Button>
                  <Button
                    variant={heroVariant === 'compact' ? 'default' : 'ghost'}
                    size="sm"
                    className="h-7 px-2 text-xs"
                    onClick={() => setHeroVariant('compact')}
                  >
                    Compact
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Side Navigation - Hidden on mobile */}
      <div className="hidden lg:flex fixed left-4 top-1/2 transform -translate-y-1/2 z-40 flex-col space-y-2">
        {featuredSections.map((section) => (
          <button
            key={section.id}
            onClick={() => scrollToSection(section.id)}
            className={`block w-3 h-3 rounded-full transition-all duration-200 ${
              currentSection === section.id
                ? 'bg-blue-600 scale-125'
                : completedSections.has(section.id)
                  ? 'bg-green-600'
                  : 'bg-gray-300 hover:bg-gray-400'
            }`}
            title={section.title}
          />
        ))}
      </div>

      {/* Mobile Progress Indicator */}
      <div className="lg:hidden fixed bottom-4 left-1/2 transform -translate-x-1/2 z-50">
        <div className="bg-white/90 backdrop-blur-xs rounded-full px-4 py-2 shadow-lg border">
          <div className="flex items-center space-x-2">
            <span className="text-xs text-gray-600 mr-2">
              {featuredSections.findIndex((s) => s.id === currentSection) + 1}/
              {featuredSections.length}
            </span>
            {featuredSections.map((section) => (
              <div
                key={section.id}
                className={`w-2 h-2 rounded-full transition-all duration-200 ${
                  currentSection === section.id
                    ? 'bg-blue-600 scale-125'
                    : completedSections.has(section.id)
                      ? 'bg-green-600'
                      : 'bg-gray-300'
                }`}
              />
            ))}
          </div>
        </div>
      </div>

      {/* Quick Navigation Menu - Hidden on medium screens and below to prevent content overlap */}
      <div className="hidden xl:block fixed right-4 top-1/2 transform -translate-y-1/2 z-40">
        <Card className="w-64 shadow-lg">
          <CardContent className="p-4">
            <h3 className="font-semibold mb-3 text-sm">Quick Navigation</h3>
            <div className="space-y-2">
              {featuredSections.map((section) => (
                <button
                  key={section.id}
                  onClick={() => scrollToSection(section.id)}
                  className={`w-full text-left p-2 rounded-lg transition-colors text-sm ${
                    currentSection === section.id
                      ? 'bg-blue-100 text-blue-900'
                      : 'hover:bg-gray-100'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <span>{section.title}</span>
                    {completedSections.has(section.id) && (
                      <CheckCircle className="w-4 h-4 text-green-600" />
                    )}
                  </div>
                </button>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Content */}
      <div className={disableInternalHeader ? `pt-[${containerTopPad}px]` : 'pt-20'}>
        {sections.map((section, index) => (
          <section key={section.id} id={`section-${section.id}`} className="min-h-screen">
            <div className="container mx-auto px-6 sm:px-8 lg:px-12 py-8 xs:py-12">
              {/* Section Header (except for hero) */}
              {section.id !== 'hero' && (
                <div className="text-center mb-8 xs:mb-12">
                  <div className="flex flex-col xs:flex-row items-center justify-center gap-2 mb-4">
                    <Badge variant="outline" className="text-xs xs:text-sm">
                      Section {index + 1} of {sections.length}
                    </Badge>
                    {completedSections.has(section.id) && (
                      <Badge className="bg-green-100 text-green-800 hover:bg-green-100 hover:text-green-800">
                        <CheckCircle className="w-3 h-3 mr-1" />
                        Viewed
                      </Badge>
                    )}
                  </div>
                  <h1 className="text-2xl xs:text-3xl sm:text-4xl font-bold mb-4">
                    {section.title}
                  </h1>
                  <p className="text-lg xs:text-xl text-muted-foreground max-w-2xl mx-auto">
                    {section.description}
                  </p>
                </div>
              )}

              {/* Section Content */}
              <div className="max-w-7xl mx-auto">{section.component}</div>

              {/* Section Navigation */}
              {index < sections.length - 1 && (
                <div className="mt-12 xs:mt-16 flex justify-center">
                  <Button
                    size="lg"
                    onClick={() => scrollToSection(sections[index + 1].id)}
                    className="bg-blue-600 hover:bg-blue-700 w-full xs:w-auto max-w-sm xs:max-w-none"
                  >
                    <span className="hidden xs:inline">Next: {sections[index + 1].title}</span>
                    <span className="xs:hidden">Next Section</span>
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              )}
            </div>
          </section>
        ))}

        {/* Final Call to Action */}
        <section className="py-16 xs:py-20 sm:py-24 bg-linear-to-br from-green-50 via-blue-50 to-purple-50">
          <div className="container mx-auto px-6 sm:px-8 lg:px-12 text-center">
            <div className="max-w-4xl mx-auto space-y-6 xs:space-y-8">
              <div className="space-y-4">
                <Badge className="bg-green-100 text-green-800 px-3 xs:px-4 py-2 hover:bg-green-100 hover:text-green-800">
                  <Star className="w-4 h-4 mr-2" />
                  Experience Complete
                </Badge>
                <h2 className="text-2xl xs:text-3xl sm:text-4xl font-bold">
                  Ready to Transform Your 406-Member Community?
                </h2>
                <p className="text-lg xs:text-xl text-muted-foreground">
                  You've experienced the power of digital governance. Now see how it can work for
                  your community.
                </p>
              </div>

              <div className="grid gap-6 xs:gap-8 sm:grid-cols-3 max-w-3xl mx-auto">
                <div className="text-center space-y-3">
                  <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto">
                    <TrendingUp className="w-8 h-8 text-blue-600" />
                  </div>
                  <h3 className="font-semibold">Days, Not Months</h3>
                  <p className="text-sm text-muted-foreground">Decision resolution time</p>
                </div>
                <div className="text-center space-y-3">
                  <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto">
                    <Users className="w-8 h-8 text-green-600" />
                  </div>
                  <h3 className="font-semibold">Every Household</h3>
                  <p className="text-sm text-muted-foreground">Can participate from any device</p>
                </div>
                <div className="text-center space-y-3">
                  <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto">
                    <Shield className="w-8 h-8 text-purple-600" />
                  </div>
                  <h3 className="font-semibold">100% Transparency</h3>
                  <p className="text-sm text-muted-foreground">Complete visibility</p>
                </div>
              </div>

              <div className="flex flex-col xs:flex-row justify-center gap-3 xs:gap-4">
                <Button
                  size="lg"
                  className="bg-green-600 hover:bg-green-700 w-full xs:w-auto"
                  onClick={() => setShowTimelineModal(true)}
                >
                  <span className="hidden xs:inline">Get Implementation Timeline</span>
                  <span className="xs:hidden">Get Timeline</span>
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
                <Button
                  size="lg"
                  variant="outline"
                  className="w-full xs:w-auto"
                  onClick={() => navigate('/schedule-demo')}
                >
                  Schedule Demo
                </Button>
                {/*
                <Button 
                  size="lg" 
                  variant="outline"
                  onClick={() => scrollToSection('hero')}
                >
                  Restart Tour
                </Button>
                */}
              </div>

              <p className="text-sm text-muted-foreground">
                Interested in testing a modern governance workflow? We're signing up pilot
                communities request a demo to join our early-access program and get priority
                support.
              </p>
            </div>
          </div>
        </section>
      </div>

      {/* Implementation Timeline Modal */}
      <ImplementationTimelineModal
        isOpen={showTimelineModal}
        onClose={() => setShowTimelineModal(false)}
      />
    </div>
  );
}
